/**
 * 实体变更广播 —— 客户端「脏标记」总线(2026-08-25 用户定)。
 *
 * 为什么要有它:看板卡片与右侧 flyout 是两份各自取数的状态。此前卡上改了数据
 * 只刷看板、flyout 里改了只刷 flyout —— 于是"卡上派了人,开着的 flyout 还显示
 * Unassigned,得关掉重开才更新"。补 prop 只能把这一处接上,下一个入口照样漏。
 *
 * 心智与 useBadgeSocket 的 `badge.invalidate` 一致:**只广播"这条脏了",不广播
 * 数据本身**,订阅方各自走原有端点重取(权限口径不变、不引第二套缓存)。
 *
 * 埋点埋在写操作的收口处 —— taskActionsService.executeAction /
 * tasksApi.updateTask / taskBatchActionService.postBatch / 物料·checklist 写接口
 * —— 所以调用方不需要记得手工通知,新加的动作入口天然联动,不会漏接。
 *
 * 三条约定:
 *  - **合并窗**:ENTITY_CHANGE_COALESCE_MS 内的多次变更合并成一次投递(批量动作
 *    刷一次,不是 N 次)。
 *  - **id 可缺省**:`PUT /pms/task-expenses/{id}` 这类端点只带行 id、拿不到 task
 *    id,此时发"通配"事件;带 id 过滤的订阅方按「可能是我」处理(宁可多刷一次,
 *    也不要漏刷 —— 漏刷就是这次要修的 bug)。
 *  - **纯叶子模块**:不 import 任何东西。api.ts 与 services/* 都要引用它,
 *    它反过来引用任何一方都会成环。
 */

export type EntityKind = "task" | "intent";

export interface EntityChangeEvent {
  entity: EntityKind;
  /** 变更实体 id;端点只带子行 id 拿不到主体 id 时省略 = 通配。 */
  id?: string | null;
}

/** 一个合并窗内、同一实体类型的全部变更。 */
export interface EntityChangeBatch {
  entity: EntityKind;
  /** 明确变更的 id 集合。 */
  ids: ReadonlySet<string>;
  /** 是否含"不知道是哪一条"的通配事件。 */
  wildcard: boolean;
  /**
   * 本批**第一条**变更的发生时刻(epoch ms)。订阅方据此判断"我手上这次取数
   * 是否已经覆盖了这批变更" —— 自己发起的动作会在广播之前就开始重取,
   * 靠它可以跳过第二发重复请求,而不必把 origin 一路透传进 service 层。
   */
  firstEmittedAt: number;
}

export type EntityChangeListener = (batch: EntityChangeBatch) => void;

/** 合并窗。250ms:足够并掉一次批量动作的连发,又不至于让人察觉延迟。 */
export const ENTITY_CHANGE_COALESCE_MS = 250;

interface PendingBatch {
  ids: Set<string>;
  wildcard: boolean;
  firstEmittedAt: number;
}

const listeners = new Set<EntityChangeListener>();
const pending = new Map<EntityKind, PendingBatch>();
let timer: ReturnType<typeof setTimeout> | null = null;

/** 写操作成功后喊一嗓子。永不抛错 —— 广播失败不该让业务调用失败。 */
export function emitEntityChanged(event: EntityChangeEvent): void {
  const { entity } = event;
  const id = event.id == null ? null : String(event.id);
  let batch = pending.get(entity);
  if (!batch) {
    batch = { ids: new Set<string>(), wildcard: false, firstEmittedAt: Date.now() };
    pending.set(entity, batch);
  }
  if (id) batch.ids.add(id);
  else batch.wildcard = true;
  if (timer === null) {
    timer = setTimeout(flushEntityChanges, ENTITY_CHANGE_COALESCE_MS);
  }
}

/** 立即投递挂起的变更(合并窗到点自动调用;测试里可手动调)。 */
export function flushEntityChanges(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (pending.size === 0) return;
  // 先快照再清空:订阅方的回调里可能又触发写操作(进而 emit),
  // 那属于下一个窗口,不该混进本次投递。
  const batches: EntityChangeBatch[] = [];
  for (const [entity, batch] of pending) {
    batches.push({
      entity,
      ids: batch.ids,
      wildcard: batch.wildcard,
      firstEmittedAt: batch.firstEmittedAt,
    });
  }
  pending.clear();
  for (const batch of batches) {
    // 逐个 try:某个订阅方抛错不该连累其它订阅方收不到。
    for (const listener of [...listeners]) {
      try {
        listener(batch);
      } catch (err) {
        console.error("[entityChangeBus] listener failed:", err);
      }
    }
  }
}

export function subscribeEntityChange(listener: EntityChangeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 这一批变更是否与订阅方相关。
 *  - 不给 id = 关心该实体的任何变更(看板列表用);
 *  - 给了 id = 只关心自己那条,但**通配事件也算命中**(见上文"宁可多刷")。
 */
export function batchMatches(
  batch: EntityChangeBatch,
  entity: EntityKind,
  id?: string | null,
): boolean {
  if (batch.entity !== entity) return false;
  if (id == null || id === "") return true;
  return batch.wildcard || batch.ids.has(String(id));
}

/** 测试隔离用:清订阅、清挂起、清定时器。 */
export function resetEntityChangeBus(): void {
  listeners.clear();
  pending.clear();
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}
